import { AsyncPipe, NgIf } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { CustomCart } from '@cart/const';
import { CustomActiveCartService } from '@cart/data-access';
import { ApplyingBonusService } from '@checkout-v2/feature';
import { concatLatestFrom } from '@ngrx/operators';
import { OnlyNumberDirective, SwitchComponent } from '@sdvor/shared/ui';
import { BonusWidgetComponent } from '@sdvor/shared/ui/v2';
import { IconModule } from '@spartacus/storefront';
import { CurrentProfileService } from '@user/feature';
import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, take } from 'rxjs/operators';

@Component({
  selector: 'sd-checkout-bonus',
  templateUrl: './checkout-bonus.component.html',
  styleUrls: ['./checkout-bonus.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgIf,
    AsyncPipe,
    ReactiveFormsModule,
    SwitchComponent,
    IconModule,
    OnlyNumberDirective,
    BonusWidgetComponent,
  ],
})
export class CheckoutBonusComponent implements OnInit, OnDestroy {
  formControl = new FormControl(undefined);
  enabled$ = new BehaviorSubject(undefined);

  maxAllowableBalance$: Observable<number>;
  balanceIsNegative$: Observable<boolean>;

  private subscription = new Subscription();

  constructor(
    private activeCartService: CustomActiveCartService,
    private currentProfileService: CurrentProfileService,
    private applyingBonusService: ApplyingBonusService,
  ) {}

  ngOnInit(): void {
    this.subscription.add(
      this.activeCartService
        .getActive()
        .pipe(
          take(1),
          map((cart: CustomCart) => cart?.bonus?.applyingAmountInfo?.value),
        )
        .subscribe((valueFromCart) => {
          this.applyingBonusService.setApplyingBonus(valueFromCart);
        }),
    );

    this.maxAllowableBalance$ = combineLatest({
      cartSubTotal: this.activeCartService.getActive().pipe(map((cart) => cart?.subTotal?.value)),
      userBonusBalance: this.currentProfileService.get().pipe(map((profile) => profile.bonusBalance.value)),
    }).pipe(
      map(({ cartSubTotal, userBonusBalance }) => {
        if (cartSubTotal < userBonusBalance) {
          return Math.floor(cartSubTotal);
        } else {
          return userBonusBalance;
        }
      }),
    );

    this.balanceIsNegative$ = this.maxAllowableBalance$.pipe(map((balance) => balance < 0));

    this.subscription.add(
      this.applyingBonusService
        .getApplyingBonus()
        .pipe(
          filter((valueFromStore) => valueFromStore !== undefined && valueFromStore !== this.formControl.value),
          take(1),
        )
        .subscribe((valueFromStore) => {
          if (!valueFromStore) {
            this.formControl.setValue(0, { emitEvent: false });
            this.enabled$.next(false);
          }

          if (valueFromStore > 0) {
            this.formControl.setValue(valueFromStore, { emitEvent: false });
            this.enabled$.next(true);
          }
        }),
    );

    this.subscription.add(
      this.enabled$
        .pipe(
          filter((isEnabled) => isEnabled !== undefined),
          distinctUntilChanged(),
          concatLatestFrom(() => this.maxAllowableBalance$),
        )
        .subscribe(([isEnabled, maxAllowableBalance]) => {
          if (isEnabled && !this.formControl.value) {
            this.formControl.setValue(maxAllowableBalance);
          }

          if (!isEnabled && this.formControl.value) {
            this.formControl.setValue(0);
          }
        }),
    );

    this.subscription.add(
      this.formControl.valueChanges
        .pipe(
          distinctUntilChanged(),
          debounceTime(500),
          concatLatestFrom(() => this.maxAllowableBalance$),
        )
        .subscribe(([appliedValue, maxAllowableBalance]) => {
          if (this.enabled$.getValue() && !appliedValue) {
            this.formControl.setValue(1);

            return;
          }

          if (this.enabled$.getValue() && appliedValue > maxAllowableBalance) {
            this.formControl.setValue(maxAllowableBalance);

            return;
          }

          this.applyingBonusService.setApplyingBonus(appliedValue);
        }),
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  enabledChanged(isEnabled: boolean): void {
    this.enabled$.next(isEnabled);
  }
}
